import axios from '../axiosConfig'
import { getEnv } from '@utils/env'
import { IClinicNumber } from '@dataTypes/clinic.type'
import { logger } from '@utils/safeLogger'
import { toApiError } from '../errors/api-error'

const BUSINESS_LAYER_API = getEnv('REACT_APP_BUSINESS_LAYER_FAX')

const headers = {
  'Content-Type': 'application/json',
}

interface ApiResponse<T> {
  statusCode: number
  message: string
  data: T
}

export type SinchNumberType = 'LOCAL' | 'TOLL_FREE' | 'MOBILE'
export type SinchSearchPattern = 'START' | 'CONTAINS' | 'END'

export interface ISinchAvailableNumber {
  phoneNumber: string
  regionCode?: string
  type?: SinchNumberType
  capability?: string[]
  setupPrice?: { amount?: string; currencyCode?: string }
  monthlyPrice?: { amount?: string; currencyCode?: string }
  supportingDocumentationRequired?: boolean
}

export interface SearchFaxNumbersParams {
  regionCode: string
  type: SinchNumberType
  pattern?: string
  searchPattern?: SinchSearchPattern
  size?: number
}

const getHeaders = (): Record<string, string> => {
  const dbName = localStorage.getItem('db_name')
  return dbName ? { ...headers, 'x-db-name': dbName } : headers
}

export const searchAvailableFaxNumbers = async (
  params: SearchFaxNumbersParams,
): Promise<ApiResponse<{ available_numbers?: ISinchAvailableNumber[] }>> => {
  try {
    const response = await axios.get(
      `${BUSINESS_LAYER_API}/api/v1/fax/numbers/available`,
      { params, headers: getHeaders() },
    )
    return response.data
  } catch (e: unknown) {
    logger.error('Error in searchAvailableFaxNumbers:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const buyFaxNumber = async (payload: {
  clinic_id: string
  phone_number: string
  display_name?: string
  service_id?: string
}): Promise<
  ApiResponse<{ number?: unknown; clinic_number?: IClinicNumber }>
> => {
  try {
    const response = await axios.post(
      `${BUSINESS_LAYER_API}/api/v1/fax/numbers/buy`,
      payload,
      { headers: getHeaders() },
    )
    return response.data
  } catch (e: unknown) {
    logger.error('Error in buyFaxNumber:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const releaseFaxNumber = async (payload: {
  clinic_number_id: string
  phone_number: string
  sinch_service_id?: string | null
}): Promise<ApiResponse<{ released?: unknown }>> => {
  try {
    const response = await axios.post(
      `${BUSINESS_LAYER_API}/api/v1/fax/numbers/release`,
      payload,
      { headers: getHeaders() },
    )
    return response.data
  } catch (e: unknown) {
    logger.error('Error in releaseFaxNumber:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const checkFaxPortability = async (
  phone_numbers: string[],
): Promise<ApiResponse<{ portability?: unknown }>> => {
  try {
    const response = await axios.post(
      `${BUSINESS_LAYER_API}/api/v1/fax/numbers/portability-check`,
      { phone_numbers },
      { headers: getHeaders() },
    )
    return response.data
  } catch (e: unknown) {
    logger.error('Error in checkFaxPortability:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const portFaxNumber = async (payload: {
  clinic_id: string
  phone_numbers: string[]
  display_name?: string
  port_in?: Record<string, unknown>
}): Promise<
  ApiResponse<{ port_in?: unknown; clinic_numbers?: IClinicNumber[] }>
> => {
  try {
    const response = await axios.post(
      `${BUSINESS_LAYER_API}/api/v1/fax/numbers/port`,
      payload,
      { headers: getHeaders() },
    )
    return response.data
  } catch (e: unknown) {
    logger.error('Error in portFaxNumber:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}
